import { useLocation } from 'react-router';
import type { PAGE_POSITIONS } from 'types';
import type { Affiliate } from 'types/capi/shared';

import type { Variant } from '~/components/Button';
import { ExternalLink } from '~/components/Button';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import type { UrlTemplates } from '~/utils/tracking';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Tracked from './Tracked';

type Props = {
  affiliate: Affiliate;
  buttonName?: TrackedButtonClickName;
  children: React.ReactNode;
  className?: string;
  pagePosition?: PAGE_POSITIONS;
  urlTemplates: UrlTemplates;
  variant?: Variant;
};

const AffiliatePageLink = ({
  children,
  affiliate,
  variant,
  urlTemplates,
  pagePosition,
  buttonName,
  className
}: Props) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(urlTemplates, { user });

  const onClick = async () => {
    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      url
    });

    await new AffiliateLinkClickEvent({
      affiliateName: affiliate.name,
      isBot
    }).send();
  };

  return pagePosition && buttonName ? (
    <Tracked.ExternalLink
      href={url}
      affiliate={affiliate.name}
      target="_blank"
      name={buttonName}
      pagePosition={pagePosition}
      rel="sponsored nofollow noopener"
      variant={variant ?? 'forest'}
      className={className}
      onClick={onClick}
    >
      {children}
    </Tracked.ExternalLink>
  ) : (
    <ExternalLink
      variant={variant ?? 'none'}
      target={'_blank'}
      rel={'sponsored nofollow noopener'}
      href={url}
      onClick={onClick}
      className={className}
    >
      {children}
    </ExternalLink>
  );
};

export default AffiliatePageLink;
